import { Status, NonClientError } from 'hooks/requests/fetchUtils';
import useFetchApi from 'hooks/useFetchApi';
import { useState, useCallback } from 'react';
import useFmcHost from 'hooks/useFmcHost';

import useQueryParameters from 'hooks/useQueryParameters';
import useApplicationBasedOverrides from 'hooks/useApplicationBasedOverrides';

import useIovation from 'hooks/useIovation';
import {
  receiveUnifiedCheckoutApplicationResult,
  UnifiedCheckoutResultType,
} from 'fmc-sdk';
import catchOnly from 'utils/catchOnly';
import { Order, ShippingAddressType } from 'state/creditApplication';
import useAnalytics, { Event } from 'hooks/useAnalytics';
const PATH = '/api/unified_checkout';

type UnifiedCheckoutHook = () => [
  (params: UnifiedCheckoutRequest) => Promise<UnifiedCheckoutResult>,
  Status,
  boolean,
];

export interface PartialError {
  error: true;
  code?: string;
  message?: string;
}

export interface UnifiedCheckoutRequest {
  firstName: string | null;
  lastName: string | null;
  emailAddress: string | null;
  mobilePhone: string | null;
  braintreeNonce?: string | null;
  braintreeCardType?: 'DEBIT' | 'CREDIT' | null;
  applicationId: string | undefined;
  paymentAgreementId: string | undefined;
  intendedPurchaseAmount: string | null;
  paymentProductType?: 'SPLITPAY' | 'INSTALLMENTS';
  disclosureChecksum: string | null;
  order: Order | null;
  shippingAddress: ShippingAddressType | null;
}

export interface UnifiedCheckoutResult {
  status: string;
  statusCodes: string[];
}

export class UnifiedCheckoutError extends Error {}

export const fraudCodes: string[] = ['040201', '040202', '040204'];
export const fraudOfacCodes: string[] = ['060301'];
export const splitpayAuthFailureRetryCodes: string[] = ['070201'];
export const fraudFailedCodes: string[] = [
  '040300',
  '040303',
  '040305',
  '040306',
];
export const kycFailureCodes: string[] = ['050400'];
export const splitpayAuthFailureNoRetryCodes: string[] = ['070202'];

type codeType =
  | 'fraud'
  | 'fraudOfac'
  | 'splitpayAuthFailureRetry'
  | 'pending'
  | 'technicalGlitch';
/**
 * Given unified checkout response statusCodes, return error scenarios with precedence:
 * 'fraud', 'fraudOfac', 'splitpayAuthFailureRetry', 'pending', technicalGlitch
 */
export const interpretDenialStatusCodes = (statusCodes: string[]): codeType => {
  if (fraudCodes.some(r => statusCodes.indexOf(r) >= 0)) {
    return 'fraud';
  } else if (fraudOfacCodes.some(r => statusCodes.indexOf(r) >= 0)) {
    return 'fraudOfac';
  } else if (
    splitpayAuthFailureRetryCodes.some(r => statusCodes.indexOf(r) >= 0)
  ) {
    return 'splitpayAuthFailureRetry';
  } else if (
    fraudFailedCodes.some(r => statusCodes.indexOf(r) >= 0) ||
    kycFailureCodes.some(r => statusCodes.indexOf(r) >= 0) ||
    splitpayAuthFailureNoRetryCodes.some(r => statusCodes.indexOf(r) >= 0)
  ) {
    return 'pending';
  } else {
    return 'technicalGlitch';
  }
};

const useUnifiedCheckout: UnifiedCheckoutHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();
  const { mockUC, location, channel, subchannel } = useQueryParameters();
  const fmcDispatch = useFmcHost();
  const { sendEvents } = useAnalytics();

  const iovationBlackbox = useIovation();
  const overrideAttributes = useApplicationBasedOverrides();

  const unifiedCheckoutRequest = useCallback(
    async (params: UnifiedCheckoutRequest): Promise<UnifiedCheckoutResult> => {
      setError(false);

      const blackbox = await iovationBlackbox;

      try {
        const responsePromise = fetch(PATH, {
          method: 'POST',
          body: {
            ...params,
            ...overrideAttributes,
            iovationBlackbox: blackbox,
            mockResponse: mockUC,
            location,
            channel,
            subchannel,
          },
        });
        sendEvents([Event.OrderSubmitted]);
        const response = await responsePromise;

        if (response.ok) {
          sendEvents([Event.OrderCompleted]);
          const result: UnifiedCheckoutResult = await response.json();
          result.status === 'APPROVED'
            ? fmcDispatch(
                receiveUnifiedCheckoutApplicationResult({
                  result: UnifiedCheckoutResultType.CheckoutCompleted,
                  statusCodes: result.statusCodes,
                }),
              )
            : fmcDispatch(
                receiveUnifiedCheckoutApplicationResult({
                  result: UnifiedCheckoutResultType.CheckoutNotCompleted,
                  statusCodes: result.statusCodes,
                }),
              );
          return Promise.resolve(result);
        } else {
          // Expect this is to be a 4xx error, in which case
          // the server is expected to return an error object.
          // So we'll attempt to parse it and get at the error message,
          // or just return the entire body if it cannot be parsed.

          fmcDispatch(
            receiveUnifiedCheckoutApplicationResult({
              result: UnifiedCheckoutResultType.Error,
              statusCodes: [],
            }),
          );

          setError(true);
          return Promise.reject(new UnifiedCheckoutError());
        }
      } catch (e) {
        if (catchOnly(e, NonClientError)) {
          // Let FmcHost know about server and network errors.

          fmcDispatch(
            receiveUnifiedCheckoutApplicationResult({
              result: UnifiedCheckoutResultType.Error,
              statusCodes: [],
            }),
          );
        }
        // Re-throw so that any exceptions can be handled downstream.
        throw e;
      }
    },
    [
      iovationBlackbox,
      fetch,
      overrideAttributes,
      mockUC,
      location,
      channel,
      subchannel,
      sendEvents,
      fmcDispatch,
    ],
  );

  return [unifiedCheckoutRequest, status, error];
};

export default useUnifiedCheckout;
